import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css'; 

const Checkout = ({ cart, clearCart, showToast, updateQuantity, removeFromCart }) => {
  const navigate = useNavigate();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const orderPlacedRef = useRef(false); 
  
  // 🟢 NEW STATE: Order Summary Dropdown के लिए
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '', 
    landmark: '', 
    pincode: '', 
    city: '', 
    state: '', 
    paymentMode: 'ONLINE' 
  });

  const statesList = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

  const updateQuantityLocal = async (cartItemId, change, currentQty) => {
    const newQty = currentQty + change;
    if (newQty < 1) return; 

    const updatedCart = cart.map(item => item.id === cartItemId ? { ...item, qty: newQty } : item);
    updateQuantity(cartItemId, change, currentQty);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (user && !formData.name) {
        setFormData(prev => ({...prev, name: user.username, email: user.email}));
    } 
    
    if (cart && cart.length === 0 && !orderPlacedRef.current) {
        showToast("⚠️ Your Cart is empty! Redirecting...");
        navigate('/'); 
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]); 

  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);
  const discountAmount = formData.paymentMode === 'ONLINE' ? Math.round(subtotal * 0.05) : 0; 
  const codCharge = formData.paymentMode === 'COD' ? 49 : 0; 
  const finalTotal = subtotal - discountAmount + codCharge; 

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🟢 Updated Pincode Logic: Manual Entry
  const handlePincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); 
    if (val.length <= 6) {
        setFormData(prev => ({ ...prev, pincode: val }));
    }
  };

  const placeOrderBackend = async (paymentId = null, razorpayOrderId = null) => {
    const baseAmount = (finalTotal / 1.05).toFixed(2); 
    const taxAmount = (finalTotal - baseAmount).toFixed(2); 

    const orderItemsPayload = cart.map(item => {
        const itemBasePrice = (item.price / 1.05).toFixed(2);
        const itemTax = (item.price - itemBasePrice).toFixed(2);
        
        return {
            id: item.productId || item.id, 
            name: item.productName || item.name,
            price: item.price, 
            basePrice: parseFloat(itemBasePrice), 
            taxAmount: parseFloat(itemTax), 
            image: item.image || item.productImage,
            qty: item.qty,
            size: item.size
        };
    });

    const finalAddress = formData.landmark 
        ? `${formData.address}, Landmark: ${formData.landmark}` 
        : formData.address;

    const orderPayload = {
        username: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: finalAddress, 
        pincode: formData.pincode,
        city: formData.city,
        state: formData.state,
        totalAmount: finalTotal, 
        baseAmount: parseFloat(baseAmount), 
        taxAmount: parseFloat(taxAmount),   
        paymentMode: formData.paymentMode,
        orderItems: JSON.stringify(orderItemsPayload), 
        razorpayPaymentId: paymentId,
        razorpayOrderId: razorpayOrderId
    };

    try {
        const response = await fetch('https://antarvaani.com/api/orders/place', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderPayload)
        });

        if(response.ok) {
            const savedOrder = await response.json();

            if (window.fbq) {
                window.fbq('track', 'Purchase', {
                    value: finalTotal, 
                    currency: 'INR'
                });
            }

            orderPlacedRef.current = true; 

            if (clearCart) clearCart(); 
            localStorage.removeItem('guestCart'); 

            const finalOrderData = {
                id: savedOrder.id,
                paymentMethod: formData.paymentMode,
                totalAmount: finalTotal, 
                name: formData.name,
                phone: formData.phone, 
                email: formData.email, 
                address: `${finalAddress}, ${formData.city}, ${formData.state} - ${formData.pincode}`,
                items: cart 
            };

            navigate('/order-confirmation', { 
                state: { orderData: finalOrderData },
                replace: true 
            });

        } else {
            showToast("❌ Failed to place order. Server Error.");
            setIsProcessing(false); 
        }
    } catch(err) {
        console.error(err);
        showToast("❌ Something went wrong.");
        setIsProcessing(false); 
    }
  };

  const handlePlaceOrder = (e) => {
    e.preventDefault();
    
    if(cart.length === 0) { 
        showToast("⚠️ Cart is empty!"); 
        return; 
    }

    if(formData.phone.length !== 10) {
        showToast("⚠️ Phone number must be exactly 10 digits!");
        return;
    }

    if(formData.address.trim().length < 8) {
        showToast("⚠️ Please enter a complete address (min 8 characters).");
        return;
    }

    if(formData.pincode.length !== 6) {
        showToast("⚠️ Please enter a valid 6-digit Pincode!");
        return;
    }

    if(!formData.state) {
        showToast("⚠️ Please select your State!");
        return;
    }

    setIsProcessing(true);

    if(formData.paymentMode === 'COD') {
        placeOrderBackend();
    } else {
        handleOnlinePayment();
    }
  };

  const handleOnlinePayment = async () => {
    try {
        const orderRes = await fetch('https://antarvaani.com/api/orders/create-razorpay-order', {
            method: 'POST',
            headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ amount: finalTotal }) 
        });
        const orderData = await orderRes.json();
        
        if(orderData.error) { 
            showToast("❌ Payment Gateway Error"); 
            setIsProcessing(false); 
            return; 
        }

        setIsProcessing(false);

        const options = {
            key: "rzp_live_S9zcGwEtjiM41d", 
            amount: orderData.amount,
            currency: "INR",
            name: "Antarvaani",
            description: "Payment for Order",
            order_id: orderData.id,
            handler: async function (response) {
                setIsProcessing(true);

                const verifyRes = await fetch('https://antarvaani.com/api/orders/verify', {
                    method: 'POST',
                    headers: {'Content-Type':'application/json'},
                    body: JSON.stringify({
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature
                    })
                });

                if(verifyRes.ok) {
                    placeOrderBackend(response.razorpay_payment_id, response.razorpay_order_id);
                } else {
                    showToast("❌ Payment Verification Failed");
                    setIsProcessing(false); 
                }
            },
            prefill: {
                name: formData.name,
                email: formData.email,
                contact: formData.phone
            },
            theme: { color: "#3399cc" }
        };

        const rzp = new window.Razorpay(options);
        
        rzp.on('payment.failed', function (response){
            showToast("❌ Payment Failed: " + response.error.description);
            setIsProcessing(false);
        });

        rzp.open();

    } catch(err) { 
        console.error(err); 
        showToast("❌ Payment Error"); 
        setIsProcessing(false); 
    }
  };

  const isLoggedIn = !!localStorage.getItem('user');

  return (
    <div className="checkout-page">
      
      {isProcessing && (
        <div className="processing-overlay">
            <img src="/loader-logo.png" alt="Processing..." />
            <h2>PROCESSING</h2>
            <p>Please wait while we confirm your order...</p>
        </div>
      )}

      <div className="checkout-layout">
        
        <div className="checkout-main">
            <div className="checkout-header">
                <h2>Antarvaani</h2>
                <p>Cart {'>'} Information {'>'} Shipping {'>'} Payment</p>
            </div>

            <form onSubmit={handlePlaceOrder}>
                <div className="checkout-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0 }}>Contact Information</h3>
                        {!isLoggedIn && (
                            <span 
                                onClick={() => navigate('/login')} 
                                style={{ color: '#0000EE', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
                            >
                                Sign in
                            </span>
                        )}
                    </div>
                    <input type="text" name="name" placeholder="Full Name" className="modern-input" value={formData.name} onChange={handleChange} required />
                    <input type="email" name="email" placeholder="Email Address" className="modern-input" value={formData.email} onChange={handleChange} required />
                    
                    <div style={{ 
                        display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden', background: '#fff', marginBottom: '15px' 
                    }}>
                        <div style={{ 
                            padding: '12px 15px', background: '#f5f5f5', borderRight: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#333', fontWeight: '500' 
                        }}>
                            <img src='/assets/header-icons/india-flag-icon.svg' alt="India Flag" style={{ width: '22px', borderRadius: '2px', objectFit: 'contain' }} /> 
                            +91
                        </div>
                        <input 
                            type="tel" 
                            name="phone" 
                            placeholder="Phone Number" 
                            style={{ border: 'none', padding: '12px', flex: 1, outline: 'none', fontSize: '0.95rem', background: 'transparent' }} 
                            value={formData.phone} 
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, ''); 
                                if(val.length <= 10) {
                                    setFormData({...formData, phone: val});
                                }
                            }} 
                            required 
                        />
                    </div>
                </div>

                <div className="checkout-section">
                    <h3>Shipping Address</h3>
                    <input 
                        type="text" name="address" 
                        placeholder="House No, Street Address" 
                        className="modern-input" 
                        value={formData.address} onChange={handleChange} required 
                    />
                    <input 
                        type="text" name="landmark" 
                        placeholder="Landmark (Optional)" 
                        className="modern-input" 
                        value={formData.landmark} onChange={handleChange} 
                    />

                    <div className="form-row">
                        <div className="third">
                            <input 
                                type="text" name="pincode" 
                                placeholder="Pincode" 
                                className="modern-input" 
                                value={formData.pincode} 
                                onChange={handlePincodeChange} 
                                required 
                                maxLength="6"
                            />
                        </div>
                        <div className="third">
                            <input 
                                type="text" name="city" 
                                placeholder="City" 
                                className="modern-input" 
                                value={formData.city} onChange={handleChange} required 
                            />
                        </div>
                        <div className="third">
                            {/* 🟢 State Dropdown */}
                            <select 
                                name="state" 
                                value={formData.state} 
                                onChange={handleChange} 
                                className="modern-input" 
                                required
                                style={{height: '45px', padding: '0 10px'}}
                            >
                                <option value="">Select State</option>
                                {statesList.map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="checkout-section">
                    <h3>Payment</h3>
                    <div className="payment-options-container">
                        <div 
                            className={`payment-method-box ${formData.paymentMode === 'ONLINE' ? 'active' : ''}`} 
                            onClick={() => setFormData({...formData, paymentMode: 'ONLINE'})}
                        >
                            <div className="payment-box-header">
                                <div className="payment-title-left">
                                    <input 
                                        type="radio" 
                                        name="payment" 
                                        checked={formData.paymentMode === 'ONLINE'} 
                                        readOnly 
                                    />
                                    <span className="payment-title-text">Razorpay Secure (UPI, Cards, Wallets)</span>
                                </div>
                                <div className="payment-icons-right" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg" alt="UPI" className="pay-icon" style={{objectFit: 'contain'}} />
                                    <img 
                                        src="/assets/visa1.png" 
                                        alt="Visa" 
                                        className="pay-icon" 
                                        style={{ objectFit: 'contain', padding: '1px 3px', height: '24px', minWidth: '38px', background: '#fff' }} 
                                    />
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="pay-icon" style={{objectFit: 'contain'}} />
                                    <span className="more-options-badge">+11</span>
                                </div>
                            </div>
                            
                            <div className="payment-box-details">
                                <p className="redirect-text">
                                    You'll be redirected to Razorpay Secure to complete your purchase.
                                </p>
                                <p className="discount-highlight-text">
                                    🎉 Flat 5% off on Prepaid
                                </p>
                            </div>
                        </div>

                        <div 
                            className={`payment-method-box ${formData.paymentMode === 'COD' ? 'active' : ''}`}
                            onClick={() => setFormData({...formData, paymentMode: 'COD'})}
                            style={{marginTop: '15px'}}
                        >
                            <div className="payment-box-header">
                                <div className="payment-title-left">
                                    <input type="radio" checked={formData.paymentMode === 'COD'} readOnly />
                                    <span className="payment-title-text">Cash on Delivery (COD)</span>
                                </div>
                            </div>
                            
                            <div className="payment-box-details">
                                <p className="cod-extra-text" style={{margin: 0, color: '#d9534f', fontWeight: 'bold', fontSize: '0.9rem'}}>
                                    Extra ₹49 for COD method
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '20px', marginBottom: '20px', borderTop: '1px solid #eaeaea', borderBottom: '1px solid #eaeaea' }}>
                    <div 
                        onClick={() => setIsOrderSummaryOpen(!isOrderSummaryOpen)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0', cursor: 'pointer' }}
                    >
                        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#111' }}>Order summary</h3>
                        <i className={`fa-solid fa-chevron-${isOrderSummaryOpen ? 'up' : 'down'}`} style={{ color: '#333', fontSize: '0.9rem' }}></i>
                    </div>
                    
                    {isOrderSummaryOpen && (
                        <div style={{ paddingBottom: '15px', animation: 'fadeIn 0.3s ease' }}>
                            {cart && cart.map((item, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                    <div style={{ position: 'relative', width: '64px', height: '64px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                                        <img src={item.image || item.productImage} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                                        <span style={{ position: 'absolute', top: '-8px', right: '-8px', background: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: '0.75rem', width: '22px', height: '22px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '50%', fontWeight: 'bold', border: '1px solid #fff' }}>
                                            {item.qty}
                                        </span>
                                    </div>
                                    <div style={{ flex: 1, paddingLeft: '15px' }}>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>{item.productName || item.name}</p>
                                        <p style={{ margin: 0, color: '#777', fontSize: '0.8rem' }}>Size: {item.size}</p>
                                    </div>
                                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333' }}>
                                        ₹{item.price * item.qty}
                                    </div>
                                </div>
                            ))}
                            
                            <div style={{ borderTop: '1px solid #eaeaea', paddingTop: '15px', marginTop: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#555', fontSize: '0.9rem' }}>
                                    <span>Subtotal</span>
                                    <span>₹{subtotal}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#555', fontSize: '0.9rem' }}>
                                    <span>Shipping</span>
                                    <span>Free</span>
                                </div>
                                {formData.paymentMode === 'ONLINE' && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'green', fontSize: '0.9rem' }}>
                                        <span>Prepaid Discount (5%)</span>
                                        <span>- ₹{discountAmount}</span>
                                    </div>
                                )}
                                {formData.paymentMode === 'COD' && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#d9534f', fontSize: '0.9rem' }}>
                                        <span>COD Charge</span>
                                        <span>+ ₹{codCharge}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '15px', borderTop: '1px solid #eaeaea', fontWeight: 'bold', fontSize: '1.2rem', color: '#000' }}>
                                    <span>Total</span>
                                    <span><span style={{ fontSize: '0.8rem', color: '#666', marginRight: '5px', fontWeight: 'normal' }}>INR</span>₹{finalTotal}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <button 
                    type="submit" 
                    className="pay-now-btn-large"
                    disabled={isProcessing} 
                    style={{ opacity: isProcessing ? 0.7 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                >
                    {isProcessing ? "PROCESSING..." : (formData.paymentMode === 'COD' ? `Complete Order - Rs. ${finalTotal}` : `Pay Now - Rs. ${finalTotal}`)}
                </button>
            </form>

            <div className="checkout-footer">
                <a href="/">Return to Store</a>
            </div>
        </div>

        <div className="checkout-sidebar">
            {cart && cart.map((item, index) => (
                <div key={index} className="sidebar-item">
                    <div className="img-badge-wrapper">
                        <img src={item.image || item.productImage} alt={item.productName || item.name} />
                        <span className="qty-badge">{item.qty}</span>
                    </div>
                    <div className="item-details">
                        <p className="name">{item.productName || item.name}</p>
                        <p className="variant">Size: {item.size}</p>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
                            <button type="button" onClick={() => updateQuantity(item.id, -1, item.qty)} style={{padding:'2px 8px'}}>-</button>
                            <span style={{fontWeight:'bold'}}>{item.qty}</span>
                            <button type="button" onClick={() => updateQuantity(item.id, 1, item.qty)} style={{padding:'2px 8px'}}>+</button>
                            <i className="fa-solid fa-trash" style={{cursor:'pointer', marginLeft:'10px', fontSize:'0.8rem', color:'#999'}} onClick={() => removeFromCart(item.id)}></i>
                        </div>
                    </div>
                    <div className="item-price">Rs. {item.price * item.qty}</div>
                </div>
            ))}

            <div className="pricing-section">
                <div className="pricing-row">
                    <span>Subtotal</span>
                    <span>Rs. {subtotal}</span>
                </div>
                <div className="pricing-row">
                    <span>Shipping</span>
                    <span>Free</span>
                </div>
                
                {formData.paymentMode === 'ONLINE' && (
                    <div className="pricing-row discount-row" style={{color: 'green', fontWeight: 'bold'}}>
                        <span>Prepaid Discount (5%)</span>
                        <span>- Rs. {discountAmount}</span>
                    </div>
                )}

                {formData.paymentMode === 'COD' && (
                    <div className="pricing-row cod-row" style={{color: '#d9534f', fontWeight: 'bold'}}>
                        <span>COD Charge</span>
                        <span>+ Rs. {codCharge}</span>
                    </div>
                )}

                <div className="total-row">
                    <span>Total</span>
                    <span className="total-amount"><span className="currency">INR</span> {finalTotal}</span>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;